import * as React from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Table, Button, ButtonToolbar } from 'components/bootstrap';
import { Timestamp, RelativeTime, ClipboardButton, Icon } from 'components/common';
import Modal from 'components/bootstrap/Modal';
import { downloadBLOB } from 'util/FileDownloadUtils';

const NoLeftPaddingTH = styled.th(
  () => css`
    padding-left: 0 !important;
  `,
);

const BreakableTD = styled.td(
  () => css`
    word-break: break-word;
  `,
);

type Props = {
  certificate: string;
  subject: string;
  issuer: string;
  sha256Fingerprint: string;
  sha1Fingerprint: string;
  validFrom: string;
  validTo: string;
};

const SAMLCertInfoTable = ({
  certificate,
  subject,
  issuer,
  sha256Fingerprint,
  sha1Fingerprint,
  validFrom,
  validTo,
}: Props) => {
  const [showModal, setShowModal] = useState<boolean>(false);

  const handleCertDownload = () => {
    downloadBLOB(certificate, {
      fileName: `certificate-${sha256Fingerprint}.pem`,
      contentType: 'application/x-pem-file',
    });
  };

  return (
    <>
      <Table condensed>
        <tbody>
          <tr>
            <NoLeftPaddingTH>Issuer</NoLeftPaddingTH>
            <BreakableTD>{issuer}</BreakableTD>
          </tr>
          <tr>
            <NoLeftPaddingTH>SHA-256 Fingerprint</NoLeftPaddingTH>
            <BreakableTD>{sha256Fingerprint}</BreakableTD>
          </tr>
          <tr>
            <NoLeftPaddingTH>Valid Until</NoLeftPaddingTH>
            <BreakableTD>
              <Timestamp dateTime={validTo} />{' '}
              <strong>
                (<RelativeTime dateTime={validTo} />)
              </strong>
            </BreakableTD>
          </tr>
          <tr>
            <NoLeftPaddingTH colSpan={2}>
              <Button bsStyle="info" bsSize="small" onClick={() => setShowModal(true)}>
                Show Certificate Details
              </Button>
            </NoLeftPaddingTH>
          </tr>
        </tbody>
      </Table>
      <Modal bsSize="large" show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header>
          <Modal.Title>Certificate Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table condensed>
            <tbody>
              <tr>
                <NoLeftPaddingTH>Subject</NoLeftPaddingTH>
                <BreakableTD>{subject}</BreakableTD>
              </tr>
              <tr>
                <NoLeftPaddingTH>Issuer</NoLeftPaddingTH>
                <BreakableTD>{issuer}</BreakableTD>
              </tr>
              <tr>
                <NoLeftPaddingTH>SHA-256 Fingerprint</NoLeftPaddingTH>
                <BreakableTD>{sha256Fingerprint}</BreakableTD>
              </tr>
              <tr>
                <NoLeftPaddingTH>SHA-1 Fingerprint</NoLeftPaddingTH>
                <BreakableTD>{sha1Fingerprint}</BreakableTD>
              </tr>
              <tr>
                <NoLeftPaddingTH>Valid From</NoLeftPaddingTH>
                <BreakableTD>
                  <Timestamp dateTime={validFrom} />
                </BreakableTD>
              </tr>
              <tr>
                <NoLeftPaddingTH>Valid Until</NoLeftPaddingTH>
                <BreakableTD>
                  <Timestamp dateTime={validTo} />{' '}
                  <strong>
                    (<RelativeTime dateTime={validTo} />)
                  </strong>
                </BreakableTD>
              </tr>
            </tbody>
          </Table>
          <strong>PEM-encoded Certificate</strong>
          <pre>{certificate}</pre>
          <ButtonToolbar>
            <ClipboardButton text={certificate} title="Copy certificate to clipboard" />
            <Button onClick={handleCertDownload}>
              <Icon name="download" type="regular" /> Download certificate
            </Button>
          </ButtonToolbar>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SAMLCertInfoTable;
